import { Injectable, NgZone, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, timer } from 'rxjs';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const UPDATE_INTERVAL_MS = 6 * 60 * MINUTE;
const ANNOUNCE_DELAY_MS = 5 * SECOND;
const AUTO_APPLY_SECONDS = 15;

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly _swUpdate = inject(SwUpdate);
  private readonly _zone = inject(NgZone);
  readonly updateAvailable = signal(false);
  readonly secondsLeft = signal(AUTO_APPLY_SECONDS);
  private _countdown: ReturnType<typeof setInterval> | null = null;

  constructor() {
    if (!this._swUpdate.isEnabled) return;

    this._swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => {
        this._zone.runOutsideAngular(() => {
          timer(ANNOUNCE_DELAY_MS).subscribe(() => this._announce());
        });
      });

    this._zone.runOutsideAngular(() => {
      timer(UPDATE_INTERVAL_MS, UPDATE_INTERVAL_MS).subscribe(() =>
        this._swUpdate.checkForUpdate(),
      );
    });
  }

  async applyUpdate(): Promise<void> {
    this._stopCountdown();
    this.updateAvailable.set(false);

    try {
      await this._swUpdate.activateUpdate();
    } finally {
      document.location.reload();
    }
  }

  private _announce(): void {
    if (this.updateAvailable()) return;

    this.updateAvailable.set(true);
    this.secondsLeft.set(AUTO_APPLY_SECONDS);

    this._countdown = setInterval(() => {
      this.secondsLeft.update((seconds) => seconds - 1);
      if (this.secondsLeft() <= 0) void this.applyUpdate();
    }, SECOND);
  }

  private _stopCountdown(): void {
    if (!this._countdown) return;

    clearInterval(this._countdown);
    this._countdown = null;
  }
}
